/**
 * Analytics hook - Initialize Google Analytics, GTM, and Facebook Pixel
 * Only initializes when environment variables are set
 */

import { useEffect } from 'react';
import config from '../config';

// Extend Window interface for analytics
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (command: string, targetId: string, config?: Record<string, unknown>) => void;
    fbq?: (command: string, action: string, options?: Record<string, unknown>) => void;
  }
}

export function useAnalytics() {
  useEffect(() => {
    // Google Analytics
    if (config.googleAnalyticsId) {
      // Load GA script
      const scriptGA = document.createElement('script');
      scriptGA.async = true;
      scriptGA.src = `https://www.googletagmanager.com/gtag/js?id=${config.googleAnalyticsId}`;
      document.head.appendChild(scriptGA);

      // Initialize GA
      const scriptGAConfig = document.createElement('script');
      scriptGAConfig.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${config.googleAnalyticsId}');
      `;
      document.head.appendChild(scriptGAConfig);

      console.log('Google Analytics initialized:', config.googleAnalyticsId);
    }

    // Google Tag Manager
    if (config.gtmId) {
      // Load GTM script
      const scriptGTM = document.createElement('script');
      scriptGTM.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${config.gtmId}');
      `;
      document.head.insertBefore(scriptGTM, document.head.firstChild);

      // Add GTM noscript
      const noscriptGTM = document.createElement('noscript');
      noscriptGTM.innerHTML = `
        <iframe src="https://www.googletagmanager.com/ns.html?id=${config.gtmId}"
        height="0" width="0" style="display:none;visibility:hidden"></iframe>
      `;
      document.body.insertBefore(noscriptGTM, document.body.firstChild);

      console.log('Google Tag Manager initialized:', config.gtmId);
    }

    // Facebook Pixel
    if (config.facebookPixelId) {
      // Load Facebook Pixel
      const scriptFB = document.createElement('script');
      scriptFB.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${config.facebookPixelId}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(scriptFB);

      // Add Facebook Pixel noscript
      const noscriptFB = document.createElement('noscript');
      noscriptFB.innerHTML = `
        <img height="1" width="1" style="display:none"
        src="https://www.facebook.com/tr?id=${config.facebookPixelId}&ev=PageView&noscript=1"/>
      `;
      document.body.insertBefore(noscriptFB, document.body.firstChild);

      console.log('Facebook Pixel initialized:', config.facebookPixelId);
    }
  }, []);

  return {
    trackEvent: (eventName: string, parameters?: Record<string, unknown>) => {
      if (config.googleAnalyticsId && window.gtag) {
        window.gtag('event', eventName, parameters);
      }
      if (config.facebookPixelId && window.fbq) {
        window.fbq('trackCustom', eventName, parameters);
      }
    },
    trackPageView: (pagePath: string) => {
      if (config.googleAnalyticsId && window.gtag) {
        window.gtag('event', 'page_view', { page_path: pagePath });
      }
      if (config.facebookPixelId && window.fbq) {
        window.fbq('track', 'PageView');
      }
    },
    trackContactFormSubmit: () => {
      if (config.googleAnalyticsId && window.gtag) {
        window.gtag('event', 'generate_lead', {
          form_name: 'contact_form'
        });
      }
      if (config.facebookPixelId && window.fbq) {
        window.fbq('track', 'Lead');
      }
    }
  };
}
